import { formatBotDuration, initializeBotRunTimer, parseBotDuration } from './botTimers.js';
import {
    applyRunningSnapshotTimer,
    hydrateRunningBotTimer,
    preserveRunningBotTimer,
} from './botRuntimeTimer.js';
import { dtoValuesEqual } from './dtoValue.js';

export { hydrateRunningBotTimer, preserveRunningBotTimer };

export const applyBotRunningLifecycle = (bot, payload = {}) => {
    if (bot.status !== 'running') initializeBotRunTimer(bot, payload.started_at);
    else applyRunningSnapshotTimer(bot, { status: 'running', started_at: payload.started_at });
    bot.status = 'running';
};

const CACHE_VERSION = 1;
const PREFIX = `corecry:my-bots:v${CACHE_VERSION}`;
const LAST_CONTEXT_KEY = `${PREFIX}:last-context`;

const cacheKey = (contextKey) => `${PREFIX}:${contextKey}`;

export const RUNTIME_PRESENTATION_FIELDS = [
    'status', 'started_at', 'duration', 'dca_runtime', 'strategy',
    'current_capital', 'total_pnl', 'pnl', 'lastSignal', 'trade_metrics', 'position_snapshot',
    'history_snapshot', 'diagnostics_snapshot',
    'runtime_state', 'snapshot_version', 'updated_at', 'safety',
    'simulation_snapshot_ready', '__startedAtMs', '__elapsedMs',
    '__statusSynced', '__lastStatusSync', '__nextStatusSyncAt'
];

const compareSnapshotVersions = (incoming, current) => {
    if (incoming == null || current == null) return 1;
    const left = String(incoming);
    const right = String(current);
    if (/^\d+$/.test(left) && /^\d+$/.test(right)) {
        if (left.length !== right.length) return left.length > right.length ? 1 : -1;
        return left === right ? 0 : (left > right ? 1 : -1);
    }
    return left === right ? 0 : (left > right ? 1 : -1);
};

export const applyBotCardSnapshot = (
    botLists,
    botId,
    snapshot = {},
    { preserveStatus = false } = {},
) => {
    if (botId == null || !snapshot || typeof snapshot !== 'object') return false;
    const targetId = String(botId);
    let changed = false;
    botLists.forEach((botList) => {
        if (!Array.isArray(botList)) return;
        botList.forEach((bot) => {
            if (bot?.id == null || String(bot.id) !== targetId) return;
            if (compareSnapshotVersions(snapshot.snapshot_version, bot.snapshot_version) < 0) return;
            const state = snapshot.runtime_state && typeof snapshot.runtime_state === 'object'
                ? snapshot.runtime_state
                : {};
            const fields = {
                ...state,
                snapshot_version: snapshot.snapshot_version,
                updated_at: snapshot.updated_at,
                safety: snapshot.safety,
                position_snapshot: snapshot.position_snapshot,
                dca_runtime: snapshot.dca_runtime,
            };
            // Summary projections default to null when no exchange snapshot exists.
            // Only an explicit runtime field or a concrete snapshot confirms a change.
            if (preserveStatus && snapshot.position_snapshot === null &&
                !Object.hasOwn(state, 'exchange_position_snapshot')) delete fields.position_snapshot;
            if (!preserveStatus) fields.status = snapshot.status;
            Object.entries(fields).forEach(([key, value]) => {
                if (value === undefined) return;
                if (!dtoValuesEqual(bot[key], value)) {
                    bot[key] = value;
                    changed = true;
                }
            });
            if (state.exchange_position_snapshot !== undefined) {
                bot.position_snapshot = state.exchange_position_snapshot;
            }
            if (state.dca_runtime !== undefined) {
                bot.dca_runtime = state.dca_runtime;
                bot.state = { ...(bot.state || {}), dca_runtime: state.dca_runtime };
            }
            if (!preserveStatus || bot.status === 'running') {
                const timerSnapshot = preserveStatus
                    ? { ...snapshot, status: bot.status }
                    : snapshot;
                changed = applyRunningSnapshotTimer(bot, timerSnapshot) || changed;
            }
            if (!dtoValuesEqual(bot.runtime_state, state)) {
                bot.runtime_state = state;
                changed = true;
            }
        });
    });
    return changed;
};

const RUNNING_ONLY_PRESENTATION_FIELDS = new Set([
    'started_at', 'dca_runtime', 'strategy', 'simulation_snapshot_ready', 'position_snapshot',
    '__startedAtMs', '__elapsedMs'
]);

const INACTIVE_STATUSES = new Set(['ready', 'stopped']);

export const restoreInactiveBotDuration = (bot, elapsedMs) => {
    if (!bot || typeof elapsedMs !== 'number' || elapsedMs < 0) return bot;
    bot.__elapsedMs = elapsedMs;
    const visibleDuration = parseBotDuration(bot.duration);
    if (elapsedMs > 0 || visibleDuration === null || visibleDuration === 0) {
        bot.duration = formatBotDuration(elapsedMs);
    }
    bot.__startedAtMs = null;
    return bot;
};

export const reconcileBotDto = (target, incoming) => {
    const next = preserveRunningBotTimer(incoming, target);
    const preserveRunningState = next?.status === 'running' && target?.status === 'running';
    Object.keys(target).forEach((key) => {
        const preservePresentation = RUNTIME_PRESENTATION_FIELDS.includes(key)
            && (!RUNNING_ONLY_PRESENTATION_FIELDS.has(key) || preserveRunningState);
        const clearRunningDuration = key === 'duration' && preserveRunningState;
        if (!Object.hasOwn(next, key) && (!preservePresentation || clearRunningDuration)) delete target[key];
    });
    Object.entries(next).forEach(([key, value]) => {
        if (!dtoValuesEqual(target[key], value)) target[key] = value;
    });
    return target;
};

export const mergeBootstrapBots = (incomingBots = [], visibleBots = []) => {
    const visibleById = new Map(
        visibleBots
            .filter((bot) => bot?.id != null)
            .map((bot) => [String(bot.id), bot])
    );

    return incomingBots.map((incoming) => {
        const visible = visibleById.get(String(incoming?.id));
        if (!visible) return incoming;

        const merged = { ...incoming };
        const incomingRunning = incoming?.status === 'running';
        const visibleRunning = visible?.status === 'running';
        const sameInactiveLifecycle = INACTIVE_STATUSES.has(incoming?.status)
            && INACTIVE_STATUSES.has(visible?.status);
        RUNTIME_PRESENTATION_FIELDS.forEach((field) => {
            if (field === 'status' && incoming?.status) return;
            if (RUNNING_ONLY_PRESENTATION_FIELDS.has(field) && !(incomingRunning && visibleRunning)) return;
            if (
                field === 'duration'
                && incoming?.status !== 'running'
                && typeof incoming?.duration === 'string'
                && incoming.duration
            ) {
                const incomingDuration = parseBotDuration(incoming.duration);
                const visibleDuration = parseBotDuration(visible.duration);
                if (
                    sameInactiveLifecycle
                    && incomingDuration === 0
                    && visibleDuration !== null
                    && visibleDuration > 0
                ) {
                    merged.duration = visible.duration;
                    if (typeof visible.__elapsedMs === 'number') {
                        merged.__elapsedMs = visible.__elapsedMs;
                    }
                }
                return;
            }
            if (visible[field] !== undefined && visible[field] !== null) {
                merged[field] = visible[field];
            }
        });
        if (incomingRunning && visibleRunning && visible.state?.dca_runtime) {
            merged.state = { ...(incoming.state || {}), dca_runtime: visible.state.dca_runtime };
        }
        return merged;
    });
};

export const markBotStoppedPresentation = (bot) => {
    bot.status = 'stopped';
    bot.dca_runtime = {};
    if (bot.state && typeof bot.state === 'object') bot.state = { ...bot.state, dca_runtime: {} };
};

export const setBotExpandedAcrossLists = (botLists, botId, expanded) => {
    if (botId == null) return;
    const targetId = String(botId);
    botLists.forEach((botList) => {
        if (!Array.isArray(botList)) return;
        botList.forEach((bot) => {
            if (bot?.id != null && String(bot.id) === targetId) {
                bot._expanded = Boolean(expanded);
            }
        });
    });
};

export const applyBotPresentationUpdate = (botLists, update = {}) => {
    if (update.botId == null) return false;
    const targetId = String(update.botId);
    if (update.snapshot && typeof update.snapshot === 'object') {
        return applyBotCardSnapshot(botLists, update.botId, update.snapshot);
    }
    let changed = false;
    botLists.forEach((botList) => {
        if (!Array.isArray(botList)) return;
        botList.forEach((bot) => {
            if (bot?.id == null || String(bot.id) !== targetId) return;
            if (update.dcaRuntime && typeof update.dcaRuntime === 'object') {
                if (
                    !dtoValuesEqual(bot.dca_runtime, update.dcaRuntime)
                    || !dtoValuesEqual(bot.state?.dca_runtime, update.dcaRuntime)
                ) {
                    bot.dca_runtime = update.dcaRuntime;
                    bot.state = { ...(bot.state || {}), dca_runtime: update.dcaRuntime };
                    changed = true;
                }
            }
            if (update.tradeMetrics && typeof update.tradeMetrics === 'object') {
                if (!dtoValuesEqual(bot.trade_metrics, update.tradeMetrics)) {
                    bot.trade_metrics = update.tradeMetrics;
                    changed = true;
                }
            }
            if (Object.hasOwn(update, 'positionSnapshot')) {
                const nextSnapshot = update.positionSnapshot || null;
                if (!dtoValuesEqual(bot.position_snapshot, nextSnapshot)) {
                    bot.position_snapshot = nextSnapshot;
                    changed = true;
                }
            }
            if (update.safety && typeof update.safety === 'object') {
                if (!dtoValuesEqual(bot.safety, update.safety)) {
                    bot.safety = update.safety;
                    changed = true;
                }
            }
            if (update.historySnapshot && typeof update.historySnapshot === 'object') {
                if (!dtoValuesEqual(bot.history_snapshot, update.historySnapshot)) {
                    bot.history_snapshot = update.historySnapshot;
                    changed = true;
                }
            }
            if (update.diagnosticsSnapshot && typeof update.diagnosticsSnapshot === 'object') {
                if (!dtoValuesEqual(bot.diagnostics_snapshot, update.diagnosticsSnapshot)) {
                    bot.diagnostics_snapshot = update.diagnosticsSnapshot;
                    changed = true;
                }
            }
        });
    });
    return changed;
};

export const applyStoredBotExpansion = (botLists, expandedState = {}) => {
    botLists.forEach((botList) => {
        if (!Array.isArray(botList)) return;
        botList.forEach((bot) => {
            if (bot?.id != null) {
                bot._expanded = Boolean(expandedState[bot.id]);
            }
        });
    });
};

export const readLastMyBotsContext = () => sessionStorage.getItem(LAST_CONTEXT_KEY);

export const readMyBotsCache = (contextKey = readLastMyBotsContext()) => {
    if (!contextKey) return null;
    try {
        const value = JSON.parse(sessionStorage.getItem(cacheKey(contextKey)) || 'null');
        if (!value || value.version !== CACHE_VERSION || value.contextKey !== contextKey) return null;
        const restore = bot => { delete bot.__runtimeSummaryState; return hydrateRunningBotTimer(bot); };
        value.bots = Array.isArray(value.bots) ? value.bots.map(restore) : [];
        value.favorites = Array.isArray(value.favorites) ? value.favorites.map(restore) : [];
        return value;
    } catch {
        return null;
    }
};

export const writeMyBotsCache = (contextKey, payload) => {
    if (!contextKey) return;
    const value = {
        version: CACHE_VERSION,
        contextKey,
        savedAt: Date.now(),
        bots: Array.isArray(payload.bots) ? payload.bots : [],
        favorites: Array.isArray(payload.favorites) ? payload.favorites : [],
        expanded: payload.expanded && typeof payload.expanded === 'object' ? payload.expanded : {},
        diagnosticsAccess: Boolean(payload.diagnosticsAccess)
    };
    sessionStorage.setItem(cacheKey(contextKey), JSON.stringify(value));
    sessionStorage.setItem(LAST_CONTEXT_KEY, contextKey);
};

export const clearMyBotsCache = (contextKey = null) => {
    if (contextKey) {
        sessionStorage.removeItem(cacheKey(contextKey));
        if (sessionStorage.getItem(LAST_CONTEXT_KEY) === contextKey) {
            sessionStorage.removeItem(LAST_CONTEXT_KEY);
        }
        return;
    }
    const keys = Array.from({ length: sessionStorage.length }, (_, index) => sessionStorage.key(index))
        .filter((key) => key && key.startsWith(`${PREFIX}:`));
    keys.forEach((key) => sessionStorage.removeItem(key));
    sessionStorage.removeItem(LAST_CONTEXT_KEY);
};
